import React, { useEffect, useMemo, useState } from "react";
import { authApi } from "./authApi";
import {
  CheckCircle2, AlertTriangle, XCircle, Minus, RefreshCw, ExternalLink,
  Pencil, Search, BadgeCheck, Loader2, Sparkles, Tags, X, MapPin, Check, ArrowRight,
  Image as ImageIcon, DownloadCloud,
} from "lucide-react";

const FIELD_LABELS = {
  ville: "Ville",
  geoloc: "Géoloc",
  texte_ia: "Texte IA",
  points_forts: "Points forts",
  infos_ville: "Infos ville",
  home_staging: "Home staging",
  seo: "SEO",
};
const FIELD_ORDER = ["ville", "geoloc", "texte_ia", "points_forts", "infos_ville", "home_staging", "seo"];

const FILTERS = [
  { id: "all", label: "Toutes" },
  { id: "complete", label: "Complètes" },
  { id: "improve", label: "À améliorer" },
  { id: "incomplete", label: "Incomplètes" },
  { id: "texte_ia", label: "Texte IA manquant" },
  { id: "infos_ville", label: "Infos ville manquantes" },
  { id: "points_forts", label: "Points forts manquants" },
  { id: "home_staging", label: "Home staging incomplet" },
  { id: "seo", label: "SEO à générer" },
];

const StatusIcon = ({ status, detail }) => {
  const common = "w-4 h-4";
  const map = {
    ok: <CheckCircle2 className={`${common} text-emerald-400`} />,
    warn: <AlertTriangle className={`${common} text-amber-400`} />,
    error: <XCircle className={`${common} text-red-400`} />,
    na: <Minus className={`${common} text-zinc-600`} />,
  };
  return <span title={detail} className="inline-flex">{map[status] || map.na}</span>;
};

const scoreColor = (status) =>
  status === "complete" ? "#34d399" : status === "improve" ? "#fbbf24" : "#f87171";
const statusLabel = (status) =>
  status === "complete" ? "Complète" : status === "improve" ? "À améliorer" : "Incomplète";

const GEN_FIELDS = ["texte_ia", "points_forts", "infos_ville", "geoloc"];
const MISSING_LABELS = {
  texte_ia: "textes IA", points_forts: "séries de points forts",
  infos_ville: "informations villes", geoloc: "géolocalisations",
  home_staging: "home staging", ville: "villes", seo: "SEO",
};

function recomputeSummary(items) {
  const total = items.length;
  const miss = {};
  items.forEach((i) => (i.missing || []).forEach((k) => { miss[k] = (miss[k] || 0) + 1; }));
  return {
    total,
    complete: items.filter((i) => i.status === "complete").length,
    improve: items.filter((i) => i.status === "improve").length,
    incomplete: items.filter((i) => i.status === "incomplete").length,
    avg_score: total ? Math.round(items.reduce((s, i) => s + i.score, 0) / total) : 0,
    missing_counts: miss,
  };
}

export function AuditBanner({ onOpen }) {
  const [s, setS] = useState(null);
  useEffect(() => { authApi.auditListings().then((r) => setS(r.summary)).catch(() => {}); }, []);
  if (!s) return null;
  const todo = (s.incomplete || 0) + (s.improve || 0);
  if (!todo) return null;
  return (
    <button onClick={onOpen} data-testid="audit-banner"
      className="w-full mb-5 flex items-center gap-3 border border-amber-500/30 bg-amber-500/[0.07] px-4 py-3 text-left hover:bg-amber-500/[0.12] transition-colors">
      <BadgeCheck className="w-5 h-5 text-amber-400 shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="text-sm text-white">{todo} annonce{todo > 1 ? "s" : ""} à compléter</span>
        <span className="text-xs text-zinc-400 ml-2">{s.incomplete || 0} incomplète(s) · {s.improve || 0} à améliorer · score moyen {s.avg_score || 0}%</span>
      </div>
      <span className="text-xs text-amber-300 inline-flex items-center gap-1 shrink-0">Ouvrir l'audit <ArrowRight className="w-3.5 h-3.5" /></span>
    </button>
  );
}

export default function AuditTab({ role, onEditGoto }) {
  const isAdmin = role === "admin" || role === "super_admin";
  const [scope, setScope] = useState("mine");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = async (sc = scope) => {
    setLoading(true);
    try {
      const res = sc === "all" ? await authApi.auditAllListings() : await authApi.auditListings();
      setData(res);
    } catch (e) {
      setData({ items: [], summary: { total: 0 } });
    } finally {
      setLoading(false);
    }
    authApi.auditCities(sc).then((r) => setCities(r.items || [])).catch(() => setCities([]));
  };
  useEffect(() => { load(scope); /* eslint-disable-next-line */ }, [scope]);

  const [cities, setCities] = useState([]);
  const [cityBusy, setCityBusy] = useState(null);
  const correctCity = async (row) => {
    if (!row.suggestion) return;
    setCityBusy(row.slug);
    try {
      const s = row.suggestion;
      const res = await authApi.auditSetCity(row.slug, {
        city: s.city, postal_code: s.postal_code, department: s.department, region: s.region, lat: s.lat, lng: s.lng,
      });
      setCities((c) => c.filter((x) => x.slug !== row.slug));
      if (res.audit) patchItem(res.audit);
    } catch (e) { /* noop */ } finally { setCityBusy(null); }
  };

  const [busy, setBusy] = useState(null);
  const [busyKind, setBusyKind] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [job, setJob] = useState(null);

  // Réparation import photos (récupérer les photos manquantes depuis l'URL source)
  const [repair, setRepair] = useState(null); // { slug, title, ...preview }
  const [repairBusy, setRepairBusy] = useState(null); // slug en cours de scan
  const [repairSel, setRepairSel] = useState({});
  const [repairApplying, setRepairApplying] = useState(false);
  const [repairMsg, setRepairMsg] = useState("");

  const openRepair = async (row) => {
    setRepairBusy(row.slug); setRepairMsg("");
    try {
      const res = await authApi.refetchPhotos(row.slug, { apply: false });
      const photos = res.new_photos || [];
      setRepair({ ...res, slug: row.slug, title: row.title });
      setRepairSel(Object.fromEntries(photos.map((_, i) => [i, true])));
    } catch (e) {
      setRepair({ slug: row.slug, title: row.title, error: e.detail?.message || e.message || "Impossible de relire la source." });
    } finally { setRepairBusy(null); }
  };

  const applyRepair = async () => {
    if (!repair) return;
    setRepairApplying(true); setRepairMsg("");
    try {
      const photos = repair.new_photos || [];
      const srcs = repair.new_sources || [];
      const images = photos.filter((_, i) => repairSel[i]);
      const sources = srcs.filter((_, i) => repairSel[i]);
      const res = await authApi.refetchPhotos(repair.slug, { apply: true, images, sources });
      setRepair(null); setRepairSel({});
      const audit = (data?.items || []).find((x) => x.slug === repair.slug);
      if (audit) patchItem({ ...audit, photos: (res.images || []).length, import: { ...(audit.import || {}), imported: (res.images || []).length, incomplete: false } });
      load();
    } catch (e) {
      setRepairMsg(e.detail?.message || e.message || "Ajout impossible.");
    } finally { setRepairApplying(false); }
  };
  const repairSelCount = Object.values(repairSel).filter(Boolean).length;

  const patchItem = (audit) => setData((d) => {
    if (!d) return d;
    const items = d.items.map((i) => i.slug === audit.slug ? { ...audit, owner_name: i.owner_name } : i);
    return { ...d, items, summary: recomputeSummary(items) };
  });

  const genRow = async (slug, kind) => {
    setBusy(slug); setBusyKind(kind);
    try {
      const res = kind === "highlights" ? await authApi.auditRegenHighlights(slug) : await authApi.auditGenerate(slug, { mode: "missing" });
      if (res.audit) patchItem(res.audit);
    } catch (e) { /* noop */ } finally { setBusy(null); setBusyKind(null); }
  };

  const openMass = async () => {
    try {
      const res = await authApi.auditCompleteAll({ scope, dry_run: true });
      setConfirm(res.summary || { to_process: 0 });
    } catch (e) { setConfirm({ to_process: 0 }); }
  };

  const runMass = async () => {
    setConfirm(null);
    try {
      const res = await authApi.auditCompleteAll({ scope, dry_run: false });
      if (res.job_id) pollJob(res.job_id);
      else load();
    } catch (e) { /* noop */ }
  };

  const pollJob = (id) => {
    setJob({ done: 0, total: 1, status: "running", current: "" });
    const timer = setInterval(async () => {
      try {
        const j = await authApi.auditJob(id);
        setJob(j);
        if (j.status === "done") { clearInterval(timer); setTimeout(() => { setJob(null); load(); }, 800); }
      } catch (e) { clearInterval(timer); setJob(null); }
    }, 4000);
  };

  const items = data?.items || [];
  const summary = data?.summary || {};

  const filtered = useMemo(() => {
    let list = items;
    if (filter === "complete" || filter === "improve" || filter === "incomplete") {
      list = list.filter((i) => i.status === filter);
    } else if (filter !== "all") {
      list = list.filter((i) => (i.missing || []).includes(filter));
    }
    const term = q.trim().toLowerCase();
    if (term) {
      list = list.filter((i) =>
        (i.title || "").toLowerCase().includes(term) ||
        (i.city || "").toLowerCase().includes(term) ||
        (i.owner_name || "").toLowerCase().includes(term));
    }
    return list;
  }, [items, filter, q]);

  const backend = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="space-y-6" data-testid="audit-tab">
      {/* En-tête */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-light text-white flex items-center gap-2">
            <BadgeCheck className="w-5 h-5 text-[#C19A6B]" /> Audit annonces
          </h2>
          <p className="text-sm text-zinc-400 mt-1">Contrôle qualité IA de votre catalogue — repérez et complétez en un coup d'œil.</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <div className="flex border border-white/15 overflow-hidden text-xs">
              <button onClick={() => setScope("mine")} data-testid="audit-scope-mine" className={`px-3 py-1.5 transition-colors ${scope === "mine" ? "bg-[#C19A6B] text-black" : "text-zinc-300 hover:bg-white/5"}`}>Mes annonces</button>
              <button onClick={() => setScope("all")} data-testid="audit-scope-all" className={`px-3 py-1.5 transition-colors ${scope === "all" ? "bg-[#C19A6B] text-black" : "text-zinc-300 hover:bg-white/5"}`}>Toutes (admin)</button>
            </div>
          )}
          <button onClick={() => load()} data-testid="audit-refresh" className="inline-flex items-center gap-1.5 text-xs border border-white/15 text-zinc-300 hover:bg-white/5 px-3 py-1.5 transition-colors">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Actualiser
          </button>
          <button onClick={openMass} data-testid="audit-complete-all" disabled={!!job} className="inline-flex items-center gap-1.5 text-xs bg-[#C19A6B] text-black hover:bg-[#d4b088] px-3 py-1.5 transition-colors disabled:opacity-50">
            <Sparkles className="w-3.5 h-3.5" /> {scope === "all" ? "Compléter les incomplètes" : "Compléter toutes mes annonces"}
          </button>
        </div>
      </div>

      {/* Progression génération en masse */}
      {job && (
        <div className="border border-[#C19A6B]/30 bg-[#C19A6B]/10 p-4" data-testid="audit-job-progress">
          <div className="flex items-center gap-2 text-sm text-white">
            <Loader2 className="w-4 h-4 animate-spin text-[#C19A6B]" />
            Complétion en cours… {job.done}/{job.total} {job.current ? `— ${job.current}` : ""}
          </div>
          <div className="h-1.5 bg-white/10 mt-2 overflow-hidden">
            <div className="h-full bg-[#C19A6B] transition-all" style={{ width: `${job.total ? Math.round((job.done / job.total) * 100) : 0}%` }} />
          </div>
        </div>
      )}

      {/* Résumé */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="audit-summary">
        <div className="border border-white/10 p-4">
          <p className="text-[11px] uppercase tracking-wider text-zinc-500">Annonces</p>
          <p className="text-2xl font-light text-white mt-1">{summary.total ?? 0}</p>
        </div>
        <div className="border border-emerald-500/25 bg-emerald-500/5 p-4">
          <p className="text-[11px] uppercase tracking-wider text-zinc-500">Complètes</p>
          <p className="text-2xl font-light text-emerald-400 mt-1">{summary.complete ?? 0}</p>
        </div>
        <div className="border border-amber-500/25 bg-amber-500/5 p-4">
          <p className="text-[11px] uppercase tracking-wider text-zinc-500">À améliorer</p>
          <p className="text-2xl font-light text-amber-400 mt-1">{summary.improve ?? 0}</p>
        </div>
        <div className="border border-red-500/25 bg-red-500/5 p-4">
          <p className="text-[11px] uppercase tracking-wider text-zinc-500">Incomplètes</p>
          <p className="text-2xl font-light text-red-400 mt-1">{summary.incomplete ?? 0}</p>
        </div>
        <div className="border border-white/10 p-4">
          <p className="text-[11px] uppercase tracking-wider text-zinc-500">Score moyen</p>
          <p className="text-2xl font-light text-[#C19A6B] mt-1">{summary.avg_score ?? 0}%</p>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex items-center gap-2 flex-wrap">
        {FILTERS.map((f) => {
          const cnt = f.id === "all" ? items.length
            : (f.id === "complete" || f.id === "improve" || f.id === "incomplete")
              ? items.filter((i) => i.status === f.id).length
              : (summary.missing_counts?.[f.id] ?? 0);
          return (
            <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`audit-filter-${f.id}`}
              className={`text-xs px-3 py-1.5 border transition-colors ${filter === f.id ? "border-[#C19A6B] bg-[#C19A6B]/10 text-white" : "border-white/10 text-zinc-400 hover:border-white/25"}`}>
              {f.label} <span className="text-zinc-500">({cnt})</span>
            </button>
          );
        })}
        <div className="relative ml-auto">
          <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="audit-search" placeholder={scope === "all" ? "Annonce, ville, agent…" : "Annonce, ville…"} className="bg-white/5 border border-white/15 pl-8 pr-3 py-1.5 text-sm text-white outline-none focus:border-[#C19A6B] w-56" />
        </div>
      </div>

      {/* Villes à vérifier */}
      {cities.length > 0 && (
        <div className="border border-amber-500/30 bg-amber-500/[0.06]" data-testid="audit-cities">
          <div className="px-4 py-3 border-b border-amber-500/20 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-amber-400" />
            <span className="text-sm text-white">Villes à vérifier</span>
            <span className="text-xs text-zinc-500">({cities.length}) — corrigez sans jamais modifier automatiquement</span>
          </div>
          <div className="divide-y divide-white/5">
            {cities.map((row) => (
              <div key={row.slug} className="px-4 py-3 flex items-center gap-3 flex-wrap" data-testid={`audit-city-${row.slug}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{row.title}</div>
                  <div className="text-xs text-zinc-400 mt-0.5">
                    Enregistré : <span className="text-amber-300">{row.stored_city || "—"}</span>
                    {row.suggestion ? (
                      <> · Suggestion : <span className="text-emerald-300">{row.suggestion.city} ({row.suggestion.postal_code})</span> <span className="text-zinc-500">— confiance {Math.round((row.confidence || 0) * 100)}%</span></>
                    ) : <span className="text-zinc-500"> · aucune commune correspondante</span>}
                  </div>
                </div>
                <a href={`${window.location.origin}/bien/${row.slug}`} target="_blank" rel="noopener noreferrer" className="p-1.5 border border-white/10 text-zinc-300 hover:border-[#C19A6B]/50 hover:text-[#C19A6B] transition-colors" title="Voir"><ExternalLink className="w-3.5 h-3.5" /></a>
                {row.suggestion && (
                  <button onClick={() => correctCity(row)} disabled={cityBusy === row.slug} data-testid={`audit-city-fix-${row.slug}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-[11px] bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 transition-colors disabled:opacity-40">
                    {cityBusy === row.slug ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Corriger
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tableau */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="border border-white/10 py-16 text-center text-zinc-500 text-sm">Aucune annonce pour ce filtre.</div>
      ) : (
        <div className="border border-white/10 overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]">
            <thead>
              <tr className="border-b border-white/10 text-[11px] uppercase tracking-wider text-zinc-500">
                <th className="text-left font-medium px-4 py-3">Annonce</th>
                <th className="font-medium px-2 py-3 text-center">Photos</th>
                {FIELD_ORDER.map((k) => <th key={k} className="font-medium px-2 py-3 text-center">{FIELD_LABELS[k]}</th>)}
                <th className="font-medium px-3 py-3 text-left w-40">Complétude</th>
                <th className="font-medium px-3 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((i) => (
                <tr key={i.slug} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`audit-row-${i.slug}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {i.cover ? <img src={i.cover} alt="" className="w-12 h-9 object-cover shrink-0" /> : <div className="w-12 h-9 bg-white/5 shrink-0" />}
                      <div className="min-w-0">
                        <div className="text-white truncate max-w-[220px]">{i.title}</div>
                        <div className="text-xs text-zinc-500 truncate">
                          {i.city || "—"}{scope === "all" && i.owner_name ? ` · ${i.owner_name}` : ""}
                          {i.published === false ? " · brouillon" : ""}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-3 text-center" data-testid={`audit-photos-${i.slug}`}>
                    {(() => {
                      const imp = i.import || {};
                      const incomplete = imp.incomplete;
                      return (
                        <div className="flex flex-col items-center gap-0.5">
                          <span className={`inline-flex items-center gap-1 text-xs ${incomplete ? "text-amber-400" : "text-zinc-300"}`}>
                            <ImageIcon className="w-3.5 h-3.5" /> {i.photos}
                            {incomplete && <AlertTriangle className="w-3 h-3 text-amber-400" />}
                          </span>
                          {imp.from_url && imp.source_detected != null && (
                            <span className="text-[9px] text-zinc-500">{imp.imported}/{imp.source_detected} source</span>
                          )}
                          {incomplete && imp.has_source_url && (
                            <button onClick={() => openRepair(i)} disabled={repairBusy === i.slug} data-testid={`audit-repair-${i.slug}`}
                              className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-amber-300 hover:text-amber-200 border border-amber-500/40 hover:border-amber-500/60 px-1.5 py-0.5 transition-colors disabled:opacity-40" title="Récupérer les photos manquantes depuis l'URL source">
                              {repairBusy === i.slug ? <Loader2 className="w-3 h-3 animate-spin" /> : <DownloadCloud className="w-3 h-3" />} Récupérer
                            </button>
                          )}
                          {!incomplete && imp.has_source_url && (
                            <button onClick={() => openRepair(i)} disabled={repairBusy === i.slug} data-testid={`audit-repair-${i.slug}`}
                              className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-zinc-400 hover:text-[#C19A6B] border border-white/10 hover:border-[#C19A6B]/50 px-1.5 py-0.5 transition-colors disabled:opacity-40" title="Vérifier la galerie source et ajouter d'éventuelles photos manquantes">
                              {repairBusy === i.slug ? <Loader2 className="w-3 h-3 animate-spin" /> : <DownloadCloud className="w-3 h-3" />} Vérifier
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  {FIELD_ORDER.map((k) => (
                    <td key={k} className="px-2 py-3 text-center" data-testid={`audit-${i.slug}-${k}`}>
                      <div className="flex flex-col items-center gap-0.5">
                        <StatusIcon status={i.fields?.[k]?.status} detail={i.fields?.[k]?.detail} />
                        {k === "home_staging" && i.staging?.photos > 0 && (
                          <span className="text-[9px] text-zinc-500">{i.staging.staged}/{i.staging.photos}×{i.staging.styles}</span>
                        )}
                      </div>
                    </td>
                  ))}
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-white/10 overflow-hidden">
                        <div className="h-full" style={{ width: `${i.score}%`, backgroundColor: scoreColor(i.status) }} />
                      </div>
                      <span className="text-xs tabular-nums" style={{ color: scoreColor(i.status) }}>{i.score}%</span>
                    </div>
                    <div className="text-[10px] mt-1" style={{ color: scoreColor(i.status) }}>{statusLabel(i.status)}</div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      {(i.missing || []).some((k) => GEN_FIELDS.includes(k)) && (
                        <button onClick={() => genRow(i.slug, "missing")} disabled={busy === i.slug} title="Générer les éléments manquants"
                          data-testid={`audit-gen-${i.slug}`}
                          className="inline-flex items-center gap-1 px-2 py-1.5 text-[11px] bg-[#C19A6B]/15 border border-[#C19A6B]/40 text-[#C19A6B] hover:bg-[#C19A6B]/25 transition-colors disabled:opacity-40">
                          {busy === i.slug && busyKind === "missing" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                          Générer
                        </button>
                      )}
                      <button onClick={() => genRow(i.slug, "highlights")} disabled={busy === i.slug} title="Régénérer les points forts (étiquettes courtes)"
                        data-testid={`audit-hl-${i.slug}`}
                        className="p-1.5 border border-white/10 text-zinc-300 hover:border-[#C19A6B]/50 hover:text-[#C19A6B] transition-colors disabled:opacity-40">
                        {busy === i.slug && busyKind === "highlights" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Tags className="w-3.5 h-3.5" />}
                      </button>
                      <a href={`${window.location.origin}/bien/${i.slug}`} target="_blank" rel="noopener noreferrer" title="Voir" data-testid={`audit-view-${i.slug}`} className="p-1.5 border border-white/10 text-zinc-300 hover:border-[#C19A6B]/50 hover:text-[#C19A6B] transition-colors"><ExternalLink className="w-3.5 h-3.5" /></a>
                      <button onClick={onEditGoto} title="Modifier" data-testid={`audit-edit-${i.slug}`} className="p-1.5 border border-white/10 text-zinc-300 hover:border-[#C19A6B]/50 hover:text-[#C19A6B] transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[11px] text-zinc-600">Légende : <span className="text-emerald-400">●</span> complet · <span className="text-amber-400">●</span> à compléter/mettre à jour · <span className="text-red-400">●</span> manquant · <span className="text-zinc-500">●</span> non applicable. « Générer » ne régénère que les éléments manquants (économie de crédits IA).</p>

      {/* Modale pré-analyse génération de masse */}
      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="audit-confirm-modal" onClick={() => setConfirm(null)}>
          <div className="bg-[#0d0d10] border border-white/15 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <h3 className="text-lg font-light text-white flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#C19A6B]" /> Analyse terminée</h3>
              <button onClick={() => setConfirm(null)} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            {confirm.to_process === 0 ? (
              <p className="text-sm text-zinc-300 mt-4">Toutes les annonces {scope === "all" ? "" : "de votre catalogue "}sont déjà complètes. Rien à générer 🎉</p>
            ) : (
              <>
                <p className="text-sm text-zinc-300 mt-4">
                  <b className="text-white">{confirm.to_process}</b> annonce(s) à compléter — <b className="text-white">{confirm.elements}</b> élément(s) manquant(s) :
                </p>
                <ul className="text-sm text-zinc-400 mt-3 space-y-1">
                  {Object.entries(confirm.missing_counts || {}).filter(([k]) => GEN_FIELDS.includes(k)).map(([k, v]) => (
                    <li key={k} className="flex items-center gap-2"><span className="w-1 h-1 bg-[#C19A6B] rounded-full" /> {v} {MISSING_LABELS[k] || k}</li>
                  ))}
                </ul>
                <p className="text-[11px] text-zinc-500 mt-3">Seuls les éléments manquants seront générés (aucun gaspillage de crédits). Le home staging et le SEO ne sont pas concernés par cette action.</p>
                {confirm.cost && confirm.cost.text_ops > 0 && (
                  <div className="mt-3 rounded-[6px] border border-[#C19A6B]/30 bg-[#C19A6B]/10 p-3" data-testid="audit-cost-estimate">
                    <p className="text-[11px] uppercase tracking-wider text-[#C19A6B] font-semibold flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> Coût IA estimé</p>
                    <div className="mt-1.5 flex items-end justify-between">
                      <span className="text-2xl font-bold text-white" data-testid="audit-cost-eur">≈ {confirm.cost.estimated_eur} €</span>
                      <span className="text-[11px] text-zinc-400 text-right">
                        {confirm.cost.text_ops} génération(s) texte<br />≈ {confirm.cost.estimated_tokens.toLocaleString("fr-FR")} tokens · {confirm.cost.model}
                      </span>
                    </div>
                    <p className="text-[10px] text-zinc-500 mt-1.5">Estimation indicative ({confirm.cost.unit_cost_eur} €/génération). La géolocalisation manquante est gratuite.</p>
                  </div>
                )}
                <div className="flex items-center justify-end gap-2 mt-5">
                  <button onClick={() => setConfirm(null)} className="text-xs border border-white/15 text-zinc-300 hover:bg-white/5 px-4 py-2">Annuler</button>
                  <button onClick={runMass} data-testid="audit-confirm-run" className="text-xs bg-[#C19A6B] text-black hover:bg-[#d4b088] px-4 py-2 font-medium">Générer les {confirm.elements} éléments</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modale réparation import — récupérer les photos manquantes */}
      {repair && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="audit-repair-modal" onClick={() => !repairApplying && setRepair(null)}>
          <div className="bg-[#0d0d10] border border-white/15 max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <h3 className="text-lg font-light text-white flex items-center gap-2"><DownloadCloud className="w-4 h-4 text-[#C19A6B]" /> Récupérer les photos manquantes</h3>
              <button onClick={() => setRepair(null)} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-zinc-400 mt-1 truncate">{repair.title}</p>
            {repair.error ? (
              <p className="text-sm text-amber-300 mt-4 border border-amber-500/30 bg-amber-500/[0.06] p-3">{repair.error}</p>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                  <div className="border border-white/10 p-3"><p className="text-[10px] uppercase tracking-wider text-zinc-500">Source</p><p className="text-xl font-light text-white">{repair.source_count ?? "—"}</p></div>
                  <div className="border border-white/10 p-3"><p className="text-[10px] uppercase tracking-wider text-zinc-500">Actuelles</p><p className="text-xl font-light text-white">{repair.current_count ?? "—"}</p></div>
                  <div className="border border-[#C19A6B]/30 bg-[#C19A6B]/10 p-3"><p className="text-[10px] uppercase tracking-wider text-zinc-500">Manquantes</p><p className="text-xl font-light text-[#C19A6B]">{repair.missing_count ?? 0}</p></div>
                </div>
                {(repair.new_photos || []).length === 0 ? (
                  <p className="text-sm text-emerald-300 mt-4">Aucune photo manquante détectée — la galerie est déjà complète 🎉</p>
                ) : (
                  <>
                    <div className="flex items-center justify-between mt-4 mb-2">
                      <p className="text-xs text-zinc-300">{repairSelCount} / {(repair.new_photos || []).length} sélectionnée(s)</p>
                      <div className="flex gap-2">
                        <button onClick={() => setRepairSel(Object.fromEntries((repair.new_photos || []).map((_, i) => [i, true])))} className="text-[11px] border border-white/15 text-zinc-300 hover:bg-white/5 px-2.5 py-1">Tout</button>
                        <button onClick={() => setRepairSel({})} className="text-[11px] border border-white/15 text-zinc-300 hover:bg-white/5 px-2.5 py-1">Aucune</button>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[42vh] overflow-y-auto pr-1" data-testid="audit-repair-grid">
                      {(repair.new_photos || []).map((u, i) => (
                        <button key={i} type="button" onClick={() => setRepairSel((s) => ({ ...s, [i]: !s[i] }))} data-testid={`audit-repair-photo-${i}`}
                          className={`relative aspect-[4/3] overflow-hidden rounded-[6px] border-2 transition-all ${repairSel[i] ? "border-[#C19A6B]" : "border-white/10 opacity-50"}`}>
                          <img src={u} alt={`Photo ${i + 1}`} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                          <span className={`absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center ${repairSel[i] ? "bg-[#C19A6B] text-black" : "bg-black/60 text-white/70"}`}>{repairSel[i] ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}</span>
                        </button>
                      ))}
                    </div>
                    {repair.debug && (
                      <details className="border border-white/10 bg-black/30 rounded-[6px] p-3 mt-3">
                        <summary className="text-[11px] uppercase tracking-wider text-[#C19A6B] cursor-pointer">Débogage (Super Admin)</summary>
                        <p className="text-[11px] text-zinc-400 mt-2">Moteur : {repair.debug.source} · retenues {repair.debug.retained ?? "—"}{repair.debug.ignored_similar_groups ? ` · ${Object.values(repair.debug.ignored_similar_groups).reduce((a, b) => a + b, 0)} de biens similaires ignorées` : ""}</p>
                      </details>
                    )}
                    <p className="text-[11px] text-zinc-500 mt-3">Les photos sélectionnées seront <b className="text-zinc-300">ajoutées</b> à la galerie existante. Le home staging, les textes et les infos IA ne sont pas modifiés.</p>
                    {repairMsg && <p className="text-xs text-amber-300 mt-2">{repairMsg}</p>}
                    <div className="flex items-center justify-end gap-2 mt-4">
                      <button onClick={() => setRepair(null)} className="text-xs border border-white/15 text-zinc-300 hover:bg-white/5 px-4 py-2">Annuler</button>
                      <button onClick={applyRepair} disabled={repairApplying || repairSelCount === 0} data-testid="audit-repair-apply" className="text-xs bg-[#C19A6B] text-black hover:bg-[#d4b088] px-4 py-2 font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
                        {repairApplying ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Ajout…</> : <><Check className="w-3.5 h-3.5" /> Ajouter les {repairSelCount} photos</>}
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
